import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {deleteAnalysisSteps} from 'analysis-tool/AnalysisActions';
import {
  AnalysisGraphQL,
  GetAnalysisPipelineStep,
  isSidebarDatasetInitializationSingleOutputStepType,
} from 'analysis-tool/AnalysisTypes';
import getDownstreamSteps from 'analysis-tool/data/getDownstreamSteps';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import isArchived from 'archive/isArchived';
import {assertAndContinue} from 'Asserts';
import {PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {DeletePipelineStepsBehavior} from 'pipelines/PipelineDAO';
import {getTableLineageAncestorSteps} from 'pipelines/StepLineageUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK} from 'ui-library/Colors';
import {Header2} from 'ui-library/Headers';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';
import {withLoadingScreen} from 'util/Promises';

import BaseStepStatusView from './BaseStepStatusView';
import {REACH_OUT_TO_BENCHLING_TEXT} from './MainViewConstants';

/**
 * Remove a failed step and all subsequent failed steps, following a confirmation modal.
 */
function removeFailedSteps(
  analysis: AnalysisGraphQL,
  step: GetAnalysisPipelineStep,
  clientStateId: string
): void {
  const parentTableStep = getTableLineageAncestorSteps(analysis, step)[0];

  // Show a more explicit message if downstream failed tables will also be removed.
  const hasDependentTopLevelTable = [...getDownstreamSteps(analysis, step.id)].some((downstreamStep) =>
    isSidebarDatasetInitializationSingleOutputStepType(downstreamStep.definition.stepType)
  );

  const typeName = hasDependentTopLevelTable
    ? 'failed table and all following failed tables'
    : 'failed table';

  runConfirmDeleteModal({typeName}).then((confirmed) => {
    if (!confirmed) {
      return;
    }
    withLoadingScreen(
      deleteAnalysisSteps(
        analysis,
        {
          stepIds: [parentTableStep.id],
          behavior: DeletePipelineStepsBehavior.DELETE_ALL_CHILDREN,
        },
        clientStateId
      )
    );
  });
}

/**
 * A button to remove the failed step, and all subsequent steps.
 *
 * Unlike the "Remove" buttons on succeeded tables and views, this button can–after a warning modal–remove
 * dependent failed top-level tables.
 */
function RemoveButton({
  step,
  analysis,
  clientStateId,
}: MainViewComponentProps<AnalysisMainViewItemType.FAILED>): JSX.Element {
  return (
    <Button
      buttonStyle={ButtonStyle.NEGATIVE}
      disabled={isArchived(analysis)}
      icon="delete"
      onClick={() => {
        removeFailedSteps(analysis, step, clientStateId);
      }}
      size={ButtonSize.SMALL}
      text="Delete"
    />
  );
}

/**
 * Component to show the view of steps that have the `FAILED` `PipelineStatusCode`.
 *
 * This view isn't universal to all error states but provides a generic UI for steps that want a `FAILED`
 * without any additional UI treatments.
 */
export default function FailedStepView(
  props: MainViewComponentProps<AnalysisMainViewItemType.FAILED>
): JSX.Element {
  const {analysis, step} = props;
  assertAndContinue(
    step.statusCode === PipelineStepStatusCode.FAILED,
    `Expected to only use this component with ${PipelineStepStatusCode.FAILED} steps`,
    {statusCode: step.statusCode, stepId: step.id, stepType: step.definition.stepType}
  );

  const showRemoveButton = !isAnalysisReadOnly(analysis);

  return (
    <BaseStepStatusView>
      <BenchlingIcon icon="error" className={css(styles.icon)} />
      <Header2>{step.statusMessage}</Header2>
      <p>{REACH_OUT_TO_BENCHLING_TEXT}</p>
      {showRemoveButton && <RemoveButton {...props} />}
    </BaseStepStatusView>
  );
}

const styles = StyleSheet.create({
  icon: {
    width: 48,
    height: 48,
    marginBottom: 15,
    color: GRAY_DARK,
  },
});
